import React from "react";
import { Link } from "react-router-dom";
import { PageContainer } from "../components/kit";

// Written from what the system actually does, not from a template: every category below is a real
// data flow (see server.py), and every category of data is one the apply profile really stores. A
// policy that lists things we do not do is as much of a problem as one that omits things we do.
//
// DELIBERATELY GENERIC ABOUT VENDORS (2026-10-14). Recipients are described by CATEGORY, not by
// company name, and the supported application systems are not enumerated. GDPR Art. 13/14 and the
// CCPA both require "recipients OR CATEGORIES of recipients", so categories are sufficient — this
// is a competitive-disclosure decision, not a compliance shortcut. Do not re-add vendor names or
// an ATS list without asking: the job-data source and the integration coverage are the two things
// a competitor would most like to read here. tests/test_privacy_page.py enforces their absence.
//
// What must NOT be genericised: the DATA CATEGORIES in the extension section. Those are certified
// to the Chrome Web Store (PII, authentication information, website content, user activity), and
// the console certification says the listing's disclosures match this page.
//
// NOT LEGAL ADVICE. Requirements differ by jurisdiction and candidates here are in Canada and the
// US; this needs a lawyer's read before it is relied on.
const UPDATED = "14 October 2026";
// A WORKING address is the only thing that matters here: vetrahub.com has no MX records, so
// privacy@vetrahub.com bounced rather than sitting unread — a privacy policy whose contact address
// rejects mail is worse than one with no address, because people rely on it. This is the operating
// company's live mailbox (verified: eminencedigital.io has a Google Workspace MX). TEMPORARY by
// intent — when Cloudflare Email Routing is enabled on vetrahub.com, point this back at
// privacy@vetrahub.com and nothing else has to change, because both mentions read from here.
const CONTACT = "info@eminencedigital.io";

// WHO THE DATA CONTROLLER IS. GDPR Art. 13(1)(a) requires the controller's identity, and the CCPA
// expects the operating business to be identifiable — "VetraHub" is a product name, not a legal
// person, so naming the entity is a compliance point rather than a courtesy. It also explains why
// the contact address is on a different domain.
//
// ENTITY_REGISTERED is deliberately EMPTY rather than guessed: publishing the wrong legal form
// ("Inc." where it is "Ltd.") misidentifies the controller in a legal document, which is worse
// than being less specific. Fill both in and the sentence completes itself — nothing else to edit.
// THREE names are in play and a reader meets all of them, so the policy ties them together rather
// than leaving the reader to guess: the product (VetraHub), the registered company, and the brand
// the contact address belongs to (eminencedigital.io). Naming only the registered company would
// make the @eminencedigital.io address MORE confusing, not less — which was the point of adding
// this section. ENTITY_REGISTERED is reproduced exactly as company records have it; do not
// re-case or abbreviate it, a legal name is not ours to tidy.
const ENTITY = "Eminence Digital";                              // trading name — matches the email domain
const ENTITY_REGISTERED = "Eminence software solution limited"; // exact registered name
const ENTITY_COUNTRY = "Canada";                                // country of registration

// Renders the fullest sentence the known facts support, and never asserts more than that: each
// clause appears only when the constant behind it is set.
function operatorSentence() {
  const name = ENTITY_REGISTERED || ENTITY;
  let out = ENTITY_COUNTRY ? `${name}, registered in ${ENTITY_COUNTRY}` : name;
  if (ENTITY_REGISTERED && ENTITY && ENTITY !== ENTITY_REGISTERED) out += `, trading as ${ENTITY}`;
  return out;
}

function H2({ children }) {
  return <h2 className="font-display font-bold text-xl text-[#0F172A] mt-10 mb-3">{children}</h2>;
}
function P({ children }) {
  return <p className="text-[15px] text-[#0F172A]/75 leading-relaxed mb-3">{children}</p>;
}
function LI({ children }) {
  return <li className="text-[15px] text-[#0F172A]/75 leading-relaxed">{children}</li>;
}
function UL({ children }) {
  return <ul className="list-disc pl-5 space-y-1.5 mb-3">{children}</ul>;
}

export default function Privacy() {
  return (
    <PageContainer>
      <div className="max-w-3xl">
        <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#0025D8] mb-1.5">Legal</div>
        <h1 className="font-display font-black text-3xl text-[#0F172A]">Privacy Policy</h1>
        <p className="text-sm text-muted-foreground mt-2">Last updated {UPDATED}</p>

        <P>
          VetraHub helps you find jobs and apply to them. To do that we handle your résumé and the
          details employers ask for on application forms. This page says what we collect, who we
          send it to, and how to get it back or delete it.
        </P>
        <P>
          VetraHub is operated by {operatorSentence()}, which is the company responsible for the
          personal data described here. “We” and “us” below mean that company.
        </P>

        <H2>What we collect</H2>
        <UL>
          <LI><strong>Your account</strong> — name, email address, and a hashed password. We never store your password itself.</LI>
          <LI><strong>Your résumé</strong> — the file you upload, and the details we read out of it: contact information, work history, education and skills.</LI>
          <LI><strong>Your apply profile</strong> — phone, address, links, years of experience, work authorisation per country, salary expectation, notice period, and saved answers to common application questions.</LI>
          <LI><strong>Voluntary disclosures</strong> — gender, ethnicity, veteran and disability status, only if you choose to provide them. Left blank, we answer “prefer not to answer” on your behalf.</LI>
          <LI><strong>What you did here</strong> — the jobs matched to you, the applications prepared, and which were sent.</LI>
        </UL>

        <H2>Who we send it to</H2>
        <P>
          We do not sell your data and we do not share it for advertising. Aside from the employer
          you are applying to, we share it only with service providers who process it on our behalf,
          each for a single purpose:
        </P>
        <UL>
          <LI><strong>Employers</strong> — this is the point of the product. When you approve an application, your résumé and answers go to that employer through the application system they use. Once an employer has your application, their privacy policy governs it, not ours.</LI>
          <LI><strong>AI providers</strong> — your résumé text and the job description, to tailor your résumé and score how well it matches. Used to produce your result, not to train models.</LI>
          <LI><strong>Job listing data providers</strong> — they receive the job titles and locations you are searching for, so we can find matching roles. They do not receive your résumé or contact details.</LI>
          <LI><strong>Payment processors</strong> — card details go to them directly; we never see or store them.</LI>
          <LI><strong>Email providers</strong> — sending our emails, and forwarding employer mail that arrives at the address described below.</LI>
          <LI><strong>Cloud hosting and storage providers</strong> — running the service and storing your uploaded files.</LI>
          <LI><strong>Application delivery services</strong> — some employers only accept applications from a real browser session, and these providers supply one so your application can be completed.</LI>
          <LI><strong>Analytics providers</strong> — which features are used, so we can improve the product.</LI>
          <LI><strong>Speech providers</strong> — speech recognition and synthesis, used only in mock interviews. Not used by the job-application features.</LI>
        </UL>
        <P>
          Each provider receives only the data it needs for its function, and is required to process
          it on our behalf rather than for its own purposes. If you want to know which providers we
          use for a particular purpose, email us and we will tell you.
        </P>

        <H2>The private email address we create for you</H2>
        <P>
          Some employers require you to create an account on their system before you can apply. For
          those we create a dedicated email address for you, based on your name, and use it as that
          account’s login. Mail sent to it is forwarded to your real email address. This keeps your
          own inbox off employer systems and lets us complete the sign-up step on your behalf. You
          can see every message we received for you inside VetraHub.
        </P>

        <H2>The browser extension</H2>
        <P>
          Our Chrome extension completes applications on sites that will only accept them from your
          own browser. It saves three things on your device: the address of our service, your
          session token, and the reference of your most recent application. While an application is
          being filled it also holds that application in memory — the details and answers being
          entered — and discards it when the application finishes or you close your browser. On the
          job sites listed in its permissions it reads the session cookie you already have, so the
          application is submitted as you rather than as an anonymous visitor. It does not read your
          browsing history and does not run on any other site.
        </P>
        <P>
          While it is filling an application it reads that employer’s form — the questions and
          field labels — and sends them to us so your saved answers can be matched to them. When
          the application is finished it sends us back what was submitted, so your record in
          VetraHub matches what the employer received.
        </P>
        <P>
          If an application fails, the extension sends us a diagnostic report so we can fix it: the
          address of the application page, the error, and a list of the network requests that page
          made. These reports are deliberately built to carry no personal details — no name, email,
          résumé or answer text — and we use them only to repair broken integrations.
        </P>

        <H2>Keeping and deleting your data</H2>
        <P>
          We keep your data for as long as your account exists. Deleting your account removes your
          profile, résumés and uploaded files, your applications and their history, any employer
          accounts we created for you, and the mail we forwarded — across every system we control.
        </P>
        <P>
          We cannot delete an application an employer has already received. Once it has been
          submitted it belongs to that employer’s records, and you would need to contact them.
        </P>

        <H2>Your rights</H2>
        <P>
          You can ask us for a copy of what we hold, correct anything wrong, or delete all of it.
          Most of this you can do yourself in Settings; for anything else, email us at
          {" "}<a href={`mailto:${CONTACT}`} className="font-semibold text-[#0025D8] hover:underline">{CONTACT}</a>{" "}
          and we will respond within 30 days. Depending on where you live you may also have the
          right to object to processing, or to complain to your local data protection authority.
        </P>

        <H2>Children</H2>
        <P>VetraHub is not intended for anyone under 16, and we do not knowingly collect their data.</P>

        <H2>Changes</H2>
        <P>
          If we change this policy we will update the date at the top, and tell you by email if the
          change is significant.
        </P>

        <H2>Contact</H2>
        <P>
          {operatorSentence()} — operator of VetraHub, and the controller of the data described on
          this page.
        </P>
        <P>
          <a href={`mailto:${CONTACT}`} className="font-semibold text-[#0025D8] hover:underline">{CONTACT}</a>
        </P>

        <div className="mt-12 pt-6 border-t border-border">
          <Link to="/" className="text-sm font-semibold text-[#0025D8] hover:underline">← Back to VetraHub</Link>
        </div>
      </div>
    </PageContainer>
  );
}
